import { useQuery } from "@tanstack/react-query";
import type { ReactNode } from "react";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { Loader } from "@/components/shared/loader";
import { AuditLogActorLabel } from "@/features/audit-logs/components/AuditLogActorLabel";
import { getAuditActionLabel } from "@/features/audit-logs/constants/action-labels";
import { auditLogService } from "@/features/audit-logs/services/auditLogService";
import {
  formatAuditPayloadLines,
  formatAuditResource,
  type AuditPayloadFormatContext,
} from "@/features/audit-logs/lib/audit-log-display";
import { formatOptionalArabicDateTime } from "@/lib/formatters";
import { queryKeys } from "@/lib/query-keys";
import type { AuditLog } from "@/types/audit-log";

function Field({ label, value }: { label: string; value: ReactNode }) {
  return (
    <div className="flex items-start justify-between gap-4 border-b border-border py-2 last:border-0">
      <span className="text-xs text-muted-foreground">{label}</span>
      <span className="text-right text-sm font-medium">{value}</span>
    </div>
  );
}

function AuditPayloadBlock({
  label,
  data,
  context,
}: {
  label: string;
  data?: unknown;
  context?: AuditPayloadFormatContext;
}) {
  const lines = formatAuditPayloadLines(data, context);

  return (
    <div className="space-y-1">
      <p className="text-xs font-medium text-muted-foreground">{label}</p>
      {lines.length > 0 ? (
        <div className="space-y-2 rounded-md bg-muted p-3">
          {lines.map((line) => (
            <div key={line.label} className="flex items-start justify-between gap-4">
              <span className="shrink-0 text-xs text-muted-foreground">{line.label}</span>
              <span className="whitespace-pre-wrap text-right text-sm font-medium">
                {line.value}
              </span>
            </div>
          ))}
        </div>
      ) : (
        <p className="text-sm text-muted-foreground">—</p>
      )}
    </div>
  );
}

interface AuditLogDetailSheetProps {
  logId: string | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  departmentName?: string;
  departmentLookup?: Record<string, string>;
  preview?: AuditLog;
}

export function AuditLogDetailSheet({
  logId,
  open,
  onOpenChange,
  departmentName,
  departmentLookup,
  preview,
}: AuditLogDetailSheetProps) {
  const { data, isLoading, error } = useQuery({
    queryKey: queryKeys.auditLogs.detail(logId ?? ""),
    queryFn: () => auditLogService.get(logId!),
    enabled: open && Boolean(logId),
  });

  const log = data ?? preview;
  const payloadContext = departmentLookup ? { departmentLookup } : undefined;

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="left" className="w-full overflow-y-auto sm:max-w-lg">
        <SheetHeader className="text-right">
          <SheetTitle>تفاصيل سجل التدقيق</SheetTitle>
          <SheetDescription className="text-right">
            {log ? getAuditActionLabel(log.action) : "—"}
          </SheetDescription>
        </SheetHeader>

        <div className="mt-6 space-y-4">
          {isLoading && (
            <div className="flex justify-center py-8">
              <Loader />
            </div>
          )}
          {error && (
            <p className="text-sm text-destructive text-right">
              تعذر تحميل تفاصيل السجل.
            </p>
          )}
          {log && !isLoading && (
            <>
              <div className="rounded-lg border border-border p-3">
                <Field label="الإجراء" value={getAuditActionLabel(log.action)} />
                <Field label="المورد" value={formatAuditResource(log)} />
                <Field label="المستخدم" value={<AuditLogActorLabel userId={log.userId} />} />
                <Field label="الدائرة" value={departmentName ?? log.departmentId ?? "—"} />
                <Field label="عنوان IP" value={log.ipAddress ?? "—"} />
                <Field label="المتصفح" value={log.userAgent ?? "—"} />
                <Field
                  label="التاريخ"
                  value={formatOptionalArabicDateTime(log.createdAt)}
                />
              </div>
              <AuditPayloadBlock label="قبل" data={log.before} context={payloadContext} />
              <AuditPayloadBlock label="بعد" data={log.after} context={payloadContext} />
            </>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
}
